/**
 * IT Reports Panel — premium enterprise Reporting Center (rpt- design system).
 * Compact category grids, segmented format selectors, per-report filters.
 * Report APIs, permissions, filters and export logic unchanged.
 */
import React, { useState, useEffect } from 'react';
import {
  FileText, Download, FileSpreadsheet, Loader, Package, Ticket, Shield, Clock, Wrench, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import './ReportsPanel.css';
import ProductInventoryReport from './ProductInventoryReport';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const ICON_MAP = {
  asset_inventory: Package,
  asset_assignments: Package,
  maintenance_report: Wrench,
  warranty_report: Shield,
  ticket_report: Ticket,
  audit_log: FileText,
  sla_compliance: Clock
};

const FORMAT_LABELS = { csv: 'CSV', excel: 'Excel', pdf: 'PDF' };

// Correct, per-report status vocabularies (backend enums)
const STATUS_OPTIONS = {
  asset_inventory: [
    ['available', 'Available'], ['assigned', 'Assigned'], ['maintenance', 'Maintenance'],
    ['pending_return', 'Pending Return'], ['retired', 'Retired']
  ],
  maintenance_report: [
    ['scheduled', 'Scheduled'], ['in_progress', 'In Progress'],
    ['completed', 'Completed'], ['cancelled', 'Cancelled']
  ],
  ticket_report: [
    ['open', 'Open'], ['in_progress', 'In Progress'], ['waiting_on_user', 'Waiting on User'],
    ['resolved', 'Resolved'], ['closed', 'Closed']
  ]
};

const PRIORITY_OPTIONS = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['urgent', 'Urgent']];

const ReportCard = ({ report, onGenerate, isGenerating }) => {
  const formats = report.formats?.length ? report.formats : ['csv', 'pdf'];
  const [format, setFormat] = useState(formats[0]);
  const [filters, setFilters] = useState({});
  const Icon = ICON_MAP[report.id] || FileText;
  const statusOpts = STATUS_OPTIONS[report.id];

  return (
    <div className="rpt-tile" data-testid={`rpt-card-${report.id}`}>
      <div className="rpt-tile-head">
        <div className="rpt-tile-icon"><Icon size={17} /></div>
        <div className="rpt-tile-title">
          <h4>{report.name}</h4>
          <p>{report.description}</p>
        </div>
      </div>

      <div className="rpt-controls">
        <div className="rpt-control">
          <label>Format</label>
          <div className="rpt-segmented" role="group">
            {formats.map(f => (
              <button
                key={f}
                type="button"
                className={format === f ? 'active' : ''}
                onClick={() => setFormat(f)}
                data-testid={`rpt-format-${report.id}-${f}`}
              >
                {FORMAT_LABELS[f] || f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {report.filters?.includes('status') && statusOpts && (
          <div className="rpt-control">
            <label>Status</label>
            <select
              value={filters.status || ''}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
              data-testid={`rpt-status-${report.id}`}
            >
              <option value="">All</option>
              {statusOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}

        {report.filters?.includes('priority') && (
          <div className="rpt-control">
            <label>Priority</label>
            <select
              value={filters.priority || ''}
              onChange={e => setFilters({ ...filters, priority: e.target.value })}
              data-testid={`rpt-priority-${report.id}`}
            >
              <option value="">All</option>
              {PRIORITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}

        {report.filters?.includes('days') && (
          <div className="rpt-control">
            <label>Period</label>
            <select
              value={filters.days || 30}
              onChange={e => setFilters({ ...filters, days: parseInt(e.target.value) })}
              data-testid={`rpt-days-${report.id}`}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        )}

        {report.filters?.includes('days_until_expiry') && (
          <div className="rpt-control">
            <label>Expiring within</label>
            <select
              value={filters.days_until_expiry || 90}
              onChange={e => setFilters({ ...filters, days_until_expiry: parseInt(e.target.value) })}
              data-testid={`rpt-expiry-${report.id}`}
            >
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
            </select>
          </div>
        )}

        {report.filters?.includes('include_history') && (
          <label className="rpt-checkbox">
            <input
              type="checkbox"
              checked={filters.include_history || false}
              onChange={e => setFilters({ ...filters, include_history: e.target.checked })}
              data-testid={`rpt-history-${report.id}`}
            />
            Include historical records
          </label>
        )}
      </div>

      <button
        className="rpt-generate"
        onClick={() => onGenerate(report, format, filters)}
        disabled={isGenerating}
        data-testid={`rpt-generate-${report.id}`}
      >
        {isGenerating
          ? <><Loader size={14} className="rpt-spin" /> Generating report...</>
          : <><Download size={14} /> Generate Report</>}
      </button>
    </div>
  );
};

const CategorySection = ({ icon: Icon, title, reports, onGenerate, generatingReport }) => {
  if (!reports.length) return null;
  return (
    <div className="rpt-category">
      <div className="rpt-category-title"><Icon size={14} /> {title}</div>
      <div className="rpt-grid">
        {reports.map(r => (
          <ReportCard key={r.id} report={r} onGenerate={onGenerate} isGenerating={generatingReport === r.id} />
        ))}
      </div>
    </div>
  );
};

const ReportsPanel = ({ isITManager }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(null);

  useEffect(() => {
    const fetchAvailableReports = async () => {
      try {
        const response = await fetch(`${API_URL}/api/it-reports/available`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (response.ok) {
          const data = await response.json();
          setReports(data.reports || []);
        }
      } catch (error) {
        console.error('Failed to fetch reports:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAvailableReports();
  }, []);

  const handleGenerateReport = async (report, format, filters) => {
    setGeneratingReport(report.id);
    const toastId = toast.loading('Generating report...');
    try {
      const params = new URLSearchParams({ format });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== undefined) params.append(key, value.toString());
      });

      const response = await fetch(`${API_URL}${report.endpoint}?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to generate report');

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `report_${Date.now()}.${format === 'excel' ? 'xlsx' : format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report ready — download started', { id: toastId });
    } catch (error) {
      toast.error('Report generation failed. Please try again.', { id: toastId });
      console.error(error);
    } finally {
      setGeneratingReport(null);
    }
  };

  if (loading) {
    return (
      <div className="rpt-loading" data-testid="rpt-loading">
        <Loader size={22} className="rpt-spin" />
        <p>Loading available reports...</p>
      </div>
    );
  }

  // IT-ASSET-REPORT-002: the consolidated product report has its own live preview panel below
  const assetReports = reports.filter(r => r.id !== 'asset_product_inventory' && (r.id.includes('asset') || r.id.includes('maintenance') || r.id.includes('warranty')));
  const ticketReports = reports.filter(r => r.id.includes('ticket'));
  const adminReports = reports.filter(r => r.id.includes('audit') || r.id.includes('sla'));

  return (
    <div className="rpt-panel" data-testid="reports-panel">
      <div className="rpt-header">
        <div className="rpt-header-left">
          <div className="rpt-header-icon"><BarChart3 size={19} /></div>
          <div>
            <h3>Reports &amp; Analytics</h3>
            <p>Generate operational, asset, ticket and compliance reports</p>
          </div>
        </div>
        <span className="rpt-count" data-testid="rpt-count">
          <FileSpreadsheet size={13} /> {reports.length} Report{reports.length === 1 ? '' : 's'} Available
        </span>
      </div>

      <CategorySection icon={Package} title="Asset Reports" reports={assetReports}
        onGenerate={handleGenerateReport} generatingReport={generatingReport} />
      {isITManager && <ProductInventoryReport />}
      <CategorySection icon={Ticket} title="Ticket Reports" reports={ticketReports}
        onGenerate={handleGenerateReport} generatingReport={generatingReport} />
      {isITManager && (
        <CategorySection icon={Shield} title="Admin & Compliance" reports={adminReports}
          onGenerate={handleGenerateReport} generatingReport={generatingReport} />
      )}
    </div>
  );
};

export default ReportsPanel;
